import isEqual from "lodash/isEqual"
import { useEffect, useRef } from "react"
import { checkIfObjectHasKeys, parseToObject } from "@/core/utils/formatters"
import { FormContentDetails, FormType } from "./types"
import FORM_CONSTANT from "@/core/app/slices/uassist/form.constant"
import { getFormByName } from "@/core/app/slices/uassist/form.util"

/**
 * React hook that runs an effect only when the deep comparison of dependencies changes.
 * @param effect The effect callback function.
 * @param deps The dependency array to deep compare.
 */
export function useDeepCompareEffect(
  effect: () => void | (() => void),
  deps: any[],
) {
  const depsRef = useRef<any[]>()
  const hasChanged = !isEqual(deps, depsRef.current)
  useEffect(() => {
    if (hasChanged) {
      depsRef.current = deps
      return effect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanged])
}

/**
 * Checks if the form meta (schema, uischema, data) is valid (i.e., all have keys).
 * @param schema The JSON schema object.
 * @param uischema The UI schema object.
 * @param data The form data object.
 * @returns True if all objects have keys, false otherwise.
 */
export function checkIfFormMetaIsValid(schema: object, uischema: object, data: object): boolean {
  return !!(
    checkIfObjectHasKeys(schema || {}) &&
    checkIfObjectHasKeys(uischema || {}) &&
    checkIfObjectHasKeys(data || {})
  )
}

/**
 * Processes a UAssist form response and returns parsed meta and saved data.
 * @param savedDataResponse The saved data response.
 * @param formResponse The form response object.
 * @returns An object containing formMeta and savedData.
 */
export function processUAssistForm(
  savedDataResponse: any,
  formResponse: any,
) {
  const {
    default_data,
    form_schema,
    ui_schema,
    save_id,
    saved_data,
    uassist_round,
  } = formResponse || {
    default_data: {},
    form_schema: {},
    ui_schema: {},
    save_id: "",
    saved_data: {},
    uassist_round: null,
  }
  const obj = {
    schema: parseToObject(form_schema?.schema_data || {}),
    uischema: parseToObject(ui_schema?.schema_data || {}),
    data: default_data ? parseToObject(default_data) : {},
    name: form_schema?.name || "",
    id: form_schema?.id || "",
    saveid: save_id || "",
    uassistRound: uassist_round,
  }
  return {
    formMeta: obj,
    savedData: savedDataResponse,
  }
}

/**
 * Gets the UAssist form ID by name from the template list.
 * @param name The form name.
 * @param formTemplateList The list of form templates.
 * @returns The form ID.
 */
export const getUassistFormIdByName = (name: string, formTemplateList: any[]): string =>
  getFormByName(formTemplateList, FormType.UASSIST, name).id

/**
 * Gets the refinement arches (upper/lower) for a case.
 * @param caseDetail The case detail object.
 * @param formTemplateList The list of form templates.
 * @param details The form details.
 * @param data The form data.
 * @returns An object with upperStr and lowerStr.
 */
export function getRefinementArches(
  caseDetail: any,
  formTemplateList: any[],
  details: FormContentDetails,
  data: any,
): { upperStr: string; lowerStr: string } {
  const udesignCategory = caseDetail?.udesign_category
  const arches = udesignCategory && udesignCategory.split("/")
  const upper = arches && arches[0]
  const lower = arches && arches[1]
  let upperStr = ""
  let lowerStr = ""

  if (
    getFormByName(formTemplateList, details?.formType as FormType, details?.formName).id ===
    getUassistFormIdByName(FORM_CONSTANT.REFINEMENT, formTemplateList)
  ) {
    lowerStr = data.arches_to_treat.includes("lower")
      ? ["A", "R"].includes(lower)
        ? lower
        : upper
      : ""
    upperStr = data.arches_to_treat.includes("upper")
      ? ["A", "R"].includes(upper)
        ? upper
        : lower
      : ""
  } else if (
    getFormByName(formTemplateList, details?.formType as FormType, details?.formName).id ===
    getUassistFormIdByName(FORM_CONSTANT.REFINEMENT_2, formTemplateList)
  ) {
    upperStr = data.arches_to_treat.upper
      ? data.arches_to_treat.upper.aligner_type === "Aligner"
        ? "A"
        : "R"
      : ""

    lowerStr = data.arches_to_treat.lower
      ? data.arches_to_treat.lower.aligner_type === "Aligner"
        ? "A"
        : "R"
      : ""
  }
  return {
    upperStr: upperStr,
    lowerStr: lowerStr,
  }
}

/**
 * Gets the UAssist arches (upper/lower) and bracket removal flag.
 * @param isRefinement Whether this is a refinement form.
 * @param isRetainer Whether this is a retainer form.
 * @param data The form data.
 * @returns An object with lower, upper, and bracket_removal.
 */
export function getuAssistArches(
  isRefinement: boolean,
  isRetainer: boolean,
  data: any,
): { lower: string; upper: string; bracket_removal?: boolean } {
  let lower: string = ""
  let upper: string = ""
  let bracket_removal: boolean | undefined
  if (!isRefinement) {
    if (isRetainer) {
      lower = data.arches_to_treat.includes("lower") ? "R" : ""
      upper = data.arches_to_treat.includes("upper") ? "R" : ""
      bracket_removal = data.bracket_removal?.value?.includes("yes")
    } else {
      upper = data.arches_to_treat.upper
        ? data.arches_to_treat.upper.aligner_type === "Aligner"
          ? "A"
          : "R"
        : ""

      lower = data.arches_to_treat.lower
        ? data.arches_to_treat.lower.aligner_type === "Aligner"
          ? "A"
          : "R"
        : ""
    }
  }

  return {
    lower: lower,
    upper: upper,
    bracket_removal: bracket_removal,
  }
}

/**
 * Validates approval status for retainer and refinement forms.
 * @param isRetainer Whether this is a retainer form.
 * @param isRefinement Whether this is a refinement form.
 * @param data The form data containing approval and final_retainer fields.
 * @returns False if approval is invalid (should submit to uassist), true if valid.
 */
export function isRetainerFastrack(
  isRetainer: boolean,
  isRefinement: boolean,
  data: any,
): boolean {
  const approvalIncludesYes = data?.approval === "yes"
  
  // Retainer: Invalid if approval exists and includes "yes"
  if (isRetainer && data?.approval && approvalIncludesYes) {
    return false
  }
  
  // Refinement validations
  if (isRefinement) {
    // Always invalid if final_retainer is "No"
    if (data?.final_retainer === "No") {
      return false
    }
    // Invalid if final_retainer is "Yes" and approval includes "yes"
    if (data?.final_retainer === "Yes" && approvalIncludesYes) {
      return false
    }
  }
  
  return true
}